import type { GlCostLine } from './apiClient';

/**
 * The GL entries behind one figure on the Financials tab, and what they add up to by cost group (#664).
 *
 * ONE filter, used by the GL drawer and by the Actual-by-cost-type view, so the two can never list a
 * different set of entries for the same cell. It is the filter the drawer always had: the cost codes
 * that formed the cell (`''` is the bucket for entries with no code, `null` is the whole job), and, on
 * a past month, only entries dated on or before that month's last day (undated entries stay in, as
 * the server cuts it).
 */
export function glLinesFor(lines: readonly GlCostLine[], codes: readonly string[] | null, through: string | null): GlCostLine[] {
  const want = codes == null ? null : new Set(codes);
  // entryDate arrives as an ISO date or datetime; its first ten characters are the day.
  const inMonth = (l: GlCostLine) => through == null || l.entryDate == null || l.entryDate.slice(0, 10) <= through;
  return lines.filter((l) => inMonth(l) && (!want || want.has(l.costCode ?? '')));
}

/** The groups in the order a reader expects them, as the Cost Type view names them. */
export const GROUP_ORDER = ['Labor', 'Material', 'Subcontract', 'Equipment', 'Other'] as const;

export interface ActualByGroup {
  /** False when the server did not say which group an entry belongs to (an API older than this view). Nothing is guessed. */
  available: boolean;
  total: number;
  /** Only the groups with a non-zero amount, in GROUP_ORDER; a group the order does not know follows them. */
  groups: Array<{ group: string; amount: number }>;
}

/**
 * Actual cost by the group the SERVER filed each entry under. The web app carries no copy of the
 * cost-type mapping, so a line without a `group` makes the whole answer unavailable instead of being
 * counted as Other, which would look plausible and be wrong.
 */
export function actualByGroup(lines: readonly GlCostLine[]): ActualByGroup {
  if (lines.some((l) => !l.group)) return { available: false, total: 0, groups: [] };
  const sums = new Map<string, number>();
  let total = 0;
  for (const l of lines) {
    sums.set(l.group!, (sums.get(l.group!) ?? 0) + l.amount);
    total += l.amount;
  }
  const known = GROUP_ORDER.filter((g) => (sums.get(g) ?? 0) !== 0).map((g) => ({ group: g as string, amount: sums.get(g)! }));
  const rest = [...sums.entries()].filter(([g, v]) => !(GROUP_ORDER as readonly string[]).includes(g) && v !== 0).map(([group, amount]) => ({ group, amount }));
  return { available: true, total, groups: [...known, ...rest] };
}
